/**
 * Eventos SSE (§12). Secuencia monótona por pelea: el cliente descarta eventos viejos
 * y todas las terminales convergen. `Last-Event-ID` permite reanudar sin pérdida.
 */
import { z } from 'zod';
import { BetSchema } from './domain/bet.js';
import { FightMetricsSchema, FightStatusSchema, CornerSchema } from './domain/fight.js';
import { PayoutSchema } from './domain/payout.js';

export const StreamEventTypeSchema = z.enum([
  'hello',
  'pool.updated',
  'ticket.created',
  'bet.created',
  'bet.paid',
  'payout.pending',
  'fight.status',
  'terminal.presence',
  'heartbeat',
  'stream.end',
]);
export type StreamEventType = z.infer<typeof StreamEventTypeSchema>;

/** Evento de la vista PÚBLICA de stream: boleto con el ciudadano enmascarado. */
export const PublicTicketEventSchema = z
  .object({
    type: z.literal('ticket.created'),
    seq: z.number().int().nonnegative(),
    ticket: z.object({
      receiptCode: z.string(),
      corner: z.enum(['red', 'blue']),
      citizenLabel: z.string().max(60),
      stakeCents: z.number().int().nonnegative(),
      oddsBp: z.number().int().positive().nullable(),
      createdAt: z.string().datetime(),
    }),
  })
  // El bus de eventos indexa por pelea: este evento es público y no la lleva.
  .extend({ fightId: z.string().optional() });
export type PublicTicketEvent = z.infer<typeof PublicTicketEventSchema>;

export const StreamEventSchema = z.discriminatedUnion('type', [
  /** También válido en `/api/v1/public/stream` (sin sesión). */
  PublicTicketEventSchema,
  z.object({
    type: z.literal('hello'),
    seq: z.number().int().nonnegative(),
    fightId: z.string(),
    serverTime: z.string(),
    /** Cada cuántos ms conviene re-sincronizar por polling si SSE cae. */
    fallbackPollMs: z.number().int().positive(),
  }),
  z.object({
    type: z.literal('pool.updated'),
    seq: z.number().int().nonnegative(),
    fightId: z.string(),
    metrics: FightMetricsSchema,
  }),
  z.object({
    type: z.literal('bet.created'),
    seq: z.number().int().nonnegative(),
    fightId: z.string(),
    bet: BetSchema,
  }),
  z.object({
    type: z.literal('bet.paid'),
    seq: z.number().int().nonnegative(),
    fightId: z.string(),
    betId: z.string(),
    payout: PayoutSchema,
  }),
  z.object({
    type: z.literal('payout.pending'),
    seq: z.number().int().nonnegative(),
    fightId: z.string(),
    pendingCount: z.number().int().nonnegative(),
    pendingCents: z.number().int().nonnegative(),
  }),
  z.object({
    type: z.literal('fight.status'),
    seq: z.number().int().nonnegative(),
    fightId: z.string(),
    status: FightStatusSchema,
    winner: CornerSchema.nullable(),
    version: z.number().int().positive(),
  }),
  z.object({
    type: z.literal('terminal.presence'),
    seq: z.number().int().nonnegative(),
    fightId: z.string(),
    terminals: z.array(
      z.object({
        id: z.string(),
        label: z.string(),
        online: z.boolean(),
      }),
    ),
  }),
  z.object({
    type: z.literal('heartbeat'),
    seq: z.number().int().nonnegative(),
    fightId: z.string(),
    serverTime: z.string(),
  }),
  /**
   * Fin DELIBERADO del stream: no hay pelea viva, o se alcanzó el tope de duración.
   *
   * El cliente NO debe reconectar: reconectar contra un stream que ya no tiene nada
   * que contar mantiene despiertas la máquina de Fly y la computa de Neon sin motivo
   * (directriz de costos ADR-0011). Es la señal de "apagá el caño".
   */
  z.object({
    type: z.literal('stream.end'),
    seq: z.number().int().nonnegative(),
    fightId: z.string().optional(),
    reason: z.enum(['no-live-fight', 'max-duration']),
  }),
]);
export type StreamEvent = z.infer<typeof StreamEventSchema>;

/** Evento de la vista PÚBLICA de stream: boleto con el ciudadano enmascarado. */
export const SSE_FALLBACK_POLL_MS = 5_000;
export const SSE_HEARTBEAT_MS = 15_000;
